import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { fetchAvailability } from "@/lib/stockAvailability";
import { formatVariantLabel, sortVariants } from "@/lib/variants";
import SearchPicker from "@/components/SearchPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const parseTr = (v) => {
  if (v == null) return NaN;
  let s = String(v).trim();
  if (!s) return NaN;
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};
const fmtQty = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 3 });
let rowSeq = 0;
const newRow = (product = null, variantId = "") => ({ key: ++rowSeq, product, variantId, warehouseId: "", qty: "" });
const rowKey = (r) => `${r.product?.id}|${r.variantId || ""}|${r.warehouseId}`;

function ItemRow({ idx, row, variants, warehouses, available, dupOf, error, onChange, onRemove, canRemove }) {
  const cache = useRef({});
  const searchProducts = async (q) => {
    const { data } = await api.get("/products", { params: { search: q, limit: 20 } });
    (data || []).forEach((p) => { cache.current[p.id] = p; });
    return data || [];
  };
  const q = parseTr(row.qty);
  const over = available !== null && Number.isFinite(q) && q > available + 1e-9;
  return (
    <div className={`grid grid-cols-12 gap-2 items-start rounded-sm border px-2 py-2 ${error ? "border-rose-600 bg-rose-950/20" : "border-slate-800"}`}
      data-testid={`so-row-${idx}`}>
      <div className="col-span-12 md:col-span-4">
        <SearchPicker testId={`so-row-${idx}-product`} value={row.product?.id || ""} selectedItem={row.product}
          onSearch={searchProducts} placeholder="Ürün ara (ad/kod/SKU)…"
          getSubtitle={(p) => p.product_code || ""}
          onChange={(id) => onChange({ product: id ? cache.current[id] || row.product : null, variantId: "" })} />
      </div>
      <div className="col-span-5 md:col-span-3">
        <Select value={row.variantId || "none"} onValueChange={(v) => onChange({ variantId: v === "none" ? "" : v })} disabled={!row.product}>
          <SelectTrigger data-testid={`so-row-${idx}-variant`} className="bg-slate-950 border-slate-700 rounded-sm h-9 text-xs">
            <SelectValue placeholder="Varyant / Beden" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
            <SelectItem value="none">Genel / varyantsız</SelectItem>
            {variants.map((v) => (
              <SelectItem key={v.id} value={v.id}>{formatVariantLabel(v.attributes, v.sku)}{v.sku ? ` · ${v.sku}` : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-4 md:col-span-2">
        <Select value={row.warehouseId || "none"} onValueChange={(v) => onChange({ warehouseId: v === "none" ? "" : v })}>
          <SelectTrigger data-testid={`so-row-${idx}-warehouse`} className="bg-slate-950 border-slate-700 rounded-sm h-9 text-xs">
            <SelectValue placeholder="Depo" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
            <SelectItem value="none" disabled>Depo seç…</SelectItem>
            {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2 md:col-span-2">
        <Input data-testid={`so-row-${idx}-qty`} inputMode="decimal" value={row.qty} placeholder="Miktar"
          onChange={(e) => onChange({ qty: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono text-xs" />
        {available !== null && (
          <div className={`text-[10px] font-mono mt-0.5 ${over ? "text-rose-400" : "text-slate-500"}`} data-testid={`so-row-${idx}-available`}>
            Kullanılabilir: {fmtQty(available)}
          </div>
        )}
      </div>
      <div className="col-span-1 flex justify-end">
        <Button variant="ghost" size="sm" disabled={!canRemove} onClick={onRemove} data-testid={`so-row-${idx}-remove`}
          className="h-9 w-9 p-0 text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
      </div>
      {(dupOf || error) && (
        <div className={`col-span-12 text-[10px] ${error ? "text-rose-300" : "text-amber-300"}`} data-testid={`so-row-${idx}-warning`}>
          {error || `Satır ${dupOf} ile aynı ürün/varyant/depo — kayıtta tek satırda birleştirilecek.`}
        </div>
      )}
    </div>
  );
}

// Toplu Opsiyon / Rezerve — mevcut stock_options + stocks.reserved altyapısı (atomik bulk endpoint).
export default function StockOptionDialog({ open, onOpenChange, initialProduct = null, initialVariantId = "", onSuccess }) {
  const { can } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [kind, setKind] = useState("option");
  const [expires, setExpires] = useState("");
  const [note, setNote] = useState("");
  const [rows, setRows] = useState([newRow()]);
  const [warehouses, setWarehouses] = useState([]);
  const [variantsBy, setVariantsBy] = useState({});
  const [avail, setAvail] = useState([]);
  const [errorRows, setErrorRows] = useState({});
  const [saving, setSaving] = useState(false);
  const custCache = useRef({});
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (!open) return;
    setCustomer(null); setKind("option"); setExpires(""); setNote(""); setErrorRows({});
    setRows([newRow(initialProduct, initialVariantId)]);
    api.get("/warehouses").then(({ data }) => setWarehouses((data || []).filter((w) => w.is_active !== false))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const productIds = useMemo(() => [...new Set(rows.map((r) => r.product?.id).filter(Boolean))], [rows]);
  useEffect(() => {
    if (!open) return;
    productIds.filter((id) => !variantsBy[id]).forEach((id) => {
      api.get(`/products/${id}/variants`).then(({ data }) => setVariantsBy((m) => ({ ...m, [id]: sortVariants(data || []) })))
        .catch(() => setVariantsBy((m) => ({ ...m, [id]: [] })));
    });
    if (productIds.length) fetchAvailability({ productIds }).then(setAvail).catch(() => setAvail([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productIds.join(",")]);

  const availFor = (r) => {
    if (!r.product || !r.warehouseId) return null;
    const a = avail.find((x) => x.product_id === r.product.id && (x.variant_id || "") === (r.variantId || "") && x.warehouse_id === r.warehouseId);
    return a ? Number(a.available_physical || 0) : 0;
  };
  const firstIdx = {};
  const dupOf = rows.map((r, i) => {
    if (!r.product || !r.warehouseId) return null;
    const k = rowKey(r);
    if (k in firstIdx) return firstIdx[k] + 1;
    firstIdx[k] = i;
    return null;
  });
  const totals = {};
  rows.forEach((r) => { if (r.product && r.warehouseId) totals[rowKey(r)] = (totals[rowKey(r)] || 0) + (parseTr(r.qty) || 0); });

  const patchRow = (i, p) => { setErrorRows({}); setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...p } : r))); };
  const searchCustomers = async (q) => {
    const { data } = await api.get("/customers/paged", { params: { q, page: 1, limit: 20 } });
    (data.items || []).forEach((c) => { custCache.current[c.id] = c; });
    return data.items || [];
  };

  const validate = () => {
    const errs = {};
    rows.forEach((r, i) => {
      const q = parseTr(r.qty);
      if (!r.product) errs[i] = "Ürün seçin";
      else if (!r.warehouseId) errs[i] = "Depo seçin";
      else if (!Number.isFinite(q) || q <= 0) errs[i] = "Miktar 0'dan büyük olmalı";
      else if (dupOf[i] == null && totals[rowKey(r)] > availFor(r) + 1e-9)
        errs[i] = `Kullanılabilir stok yetersiz: ${fmtQty(availFor(r))}, istenen ${fmtQty(totals[rowKey(r)])}`;
    });
    return errs;
  };

  const submit = async () => {
    if (!customer) { toast.error("Firma / müşteri seçin"); return; }
    if (kind === "option" && !expires) { toast.error("Opsiyon bitiş tarihi seçin"); return; }
    const errs = validate();
    setErrorRows(errs);
    if (Object.keys(errs).length) { toast.error(`Satır ${Object.keys(errs).map((i) => Number(i) + 1).join(", ")}: kontrol edin — hiçbir satır kaydedilmedi`); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/inventory/stock-options/bulk", {
        customer_id: customer.id, kind, expires_at: kind === "option" ? expires : "", note,
        items: rows.map((r) => ({ product_id: r.product.id, variant_id: r.variantId || "", warehouse_id: r.warehouseId, quantity: parseTr(r.qty) })),
      });
      toast.success(`${data.count} kalem ${kind === "option" ? "opsiyonlandı" : "rezerve edildi"} — ${customer.name}`);
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      const msg = formatApiError(e);
      const m = /Satır ([\d, ]+):\s*(.*)/.exec(msg);
      if (m) setErrorRows(Object.fromEntries(m[1].split(",").map((n) => [Number(n.trim()) - 1, m[2]])));
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="so-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-100">Opsiyon / Rezerve</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Fiziksel stok değişmez; miktar ayrılır ve kullanılabilir stok düşer. Yoldaki stok ve başka depolar kullanılmaz. Kayıt atomiktir.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Firma / Müşteri *</Label>
            <SearchPicker testId="so-customer" value={customer?.id || ""} selectedItem={customer} onSearch={searchCustomers}
              placeholder="Müşteri adı, VKN, telefon…" getSubtitle={(c) => c.phone || c.tax_no || ""}
              onChange={(id) => setCustomer(id ? custCache.current[id] || null : null)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">İşlem Tipi *</Label>
            <div className="flex gap-1">
              {[["option", "Opsiyon"], ["reserve", "Rezerve"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`so-kind-${v}`} onClick={() => setKind(v)}
                  className={`flex-1 h-9 rounded-sm border text-xs font-semibold uppercase tracking-wider transition-colors ${kind === v ? "border-sky-500 bg-sky-950/50 text-sky-300" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>{l}</button>
              ))}
            </div>
          </div>
          {kind === "option" && (
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Opsiyon Bitiş *</Label>
              <Input data-testid="so-expires" type="date" min={today} value={expires} onChange={(e) => setExpires(e.target.value)} className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono" />
            </div>
          )}
          <div className={`${kind === "option" ? "md:col-span-2" : "md:col-span-3"} space-y-1.5`}>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Not</Label>
            <Textarea data-testid="so-note" value={note} onChange={(e) => setNote(e.target.value)} rows={1} className="bg-slate-950 border-slate-700 rounded-sm text-sm min-h-9" />
          </div>
        </div>
        <div className="space-y-2 mt-2" data-testid="so-rows">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kalemler</div>
          {rows.map((r, i) => (
            <ItemRow key={r.key} idx={i + 1} row={r} warehouses={warehouses} variants={variantsBy[r.product?.id] || []}
              available={availFor(r)} dupOf={dupOf[i]} error={errorRows[i]} canRemove={rows.length > 1}
              onChange={(p) => patchRow(i, p)} onRemove={() => { setErrorRows({}); setRows((rs) => rs.filter((_, j) => j !== i)); }} />
          ))}
          <Button variant="outline" size="sm" data-testid="so-add-row" onClick={() => setRows((rs) => [...rs, newRow()])}
            className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" /> Satır Ekle
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm" data-testid="so-cancel">İptal</Button>
          <Button onClick={submit} disabled={(saving) || !(can("products", "edit"))} title={!(can("products", "edit")) ? "Stok opsiyonu yetkiniz yok (products.edit)" : undefined} className="bg-sky-600 hover:bg-sky-500 rounded-sm" data-testid="so-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {kind === "option" ? "Opsiyonla" : "Rezerve Et"} ({rows.length} satır)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
